/**
 * Admin: Routes & Regions — group properties into city regions, build job routes,
 * view all properties on a map (Leaflet) color-coded by region.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Map as MapIcon, Plus, Trash2, MapPin, Route as RouteIcon, Compass, Mail, Save } from 'lucide-react';
import { toast } from 'sonner';
import CitiesRegionsExplorer from './CitiesRegionsExplorer';

import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RegionalTerritoryEditor } from './admin/RegionalTerritoryEditor';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix default marker icons (Leaflet's default icons fail under bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Dallas-Fort Worth default center
const DEFAULT_CENTER = [32.9, -96.95];
const DEFAULT_ZOOM = 9;

// ── Big-City map presets ─────────────────────────────────────────────
// Per CEO directive Feb 2026: the Routes & Regions map must recenter to
// the picked Big City so pins stay readable and never overload the
// viewport. Coords are the metro downtowns; zoom keeps the whole metro
// visible (~40 mile radius).
const BIG_CITY_MAP = {
  Dallas: { center: [32.7767, -96.7970], zoom: 10 },
  Houston: { center: [29.7604, -95.3698], zoom: 10 },
  Austin: { center: [30.2672, -97.7431], zoom: 10 },
  'San Antonio': { center: [29.4241, -98.4936], zoom: 10 },
  Tyler: { center: [32.3513, -95.3011], zoom: 10 },
};

// Small child of MapContainer — flies the map to the current selection
// whenever the cascade filter changes. Priority:
//   1. Region drill-down → fit bounds to those specific pins.
//   2. Big-City selected → use the Big-City preset (Dallas / Austin / etc.)
//      so bad-geocoded outliers never zoom the map out to the globe.
//   3. Otherwise → default DFW view.
function MapFlyController({ pins, bigCity, regionSelected }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    if (regionSelected && pins && pins.length > 1) {
      try {
        const bounds = L.latLngBounds(pins.map((p) => [p.latitude, p.longitude]));
        map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 13, duration: 0.6 });
        return;
      } catch (_) { /* fall through */ }
    }
    if (regionSelected && pins && pins.length === 1) {
      map.flyTo([pins[0].latitude, pins[0].longitude], 13, { duration: 0.6 });
      return;
    }
    const preset = BIG_CITY_MAP[bigCity];
    if (preset) {
      map.flyTo(preset.center, preset.zoom, { duration: 0.6 });
    } else {
      map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 0.6 });
    }
  }, [map, pins, bigCity, regionSelected]);
  return null;
}

// Reject clearly invalid geocode results (0,0 or Europe/Africa hits) so a
// stray property with a bad address never overloads the map bounds. NECS
// operates in the continental US only right now — expand this box when
// international expansion is scoped.
const US_LAT = [24.5, 49.5];
const US_LNG = [-125.0, -66.5];
function isUsCoord(p) {
  return (
    typeof p.latitude === 'number' && typeof p.longitude === 'number'
    && p.latitude >= US_LAT[0] && p.latitude <= US_LAT[1]
    && p.longitude >= US_LNG[0] && p.longitude <= US_LNG[1]
  );
}

export default function AdminRoutesRegions({ readOnly = false, deptLabel = null } = {}) {
  const [regions, setRegions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [staff, setStaff] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [regionDialog, setRegionDialog] = useState({ open: false, edit: null });
  const [routeDialog, setRouteDialog] = useState({ open: false });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, p, u, rt] = await Promise.all([
        fetch(`${API}/api/routes/regions`, { credentials: 'include' }),
        fetch(`${API}/api/properties?operational_only=true`, { credentials: 'include' }),
        fetch(`${API}/api/employees`, { credentials: 'include' }).catch(() => null),
        fetch(`${API}/api/routes`, { credentials: 'include' }),
      ]);
      if (r?.ok) setRegions(await r.json());
      if (p?.ok) setProperties(await p.json());
      if (u?.ok) setStaff(await u.json());
      if (rt?.ok) setRoutes(await rt.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    window.addEventListener('necs-commercial-handoff', loadAll);
    return () => window.removeEventListener('necs-commercial-handoff', loadAll);
  }, []);

  const [mapOpen, setMapOpen] = useState(false);

  // ── Big City tab-list filter (CEO directive Feb 2026) ────────────────
  // The tab pills above the map drive BOTH the map viewport and the
  // Cities & Regions Explorer below. Fixed catalog of the 5 core TX
  // metros — extensible as NECS expands. Auto-selects Dallas as the
  // Dallas-focused launch metro.
  const BIG_CITY_TABS = ['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'];
  const [bigCityFilter, setBigCityFilter] = useState('Dallas');
  // Secondary state selector kept for future multi-state expansion.
  const [stateFilter, setStateFilter] = useState('__ALL__');

  const availableStates = useMemo(() => {
    const seen = new Set();
    regions.forEach((r) => { if (r.state) seen.add(r.state); });
    return Array.from(seen).sort();
  }, [regions]);

  // Regions in the currently-selected Big City scope. Used to filter
  // both the map pins AND the Cities & Regions Explorer below.
  const scopedRegions = useMemo(() => {
    return regions
      .filter((r) => stateFilter === '__ALL__' || r.state === stateFilter)
      .filter((r) => (r.big_city || 'Unassigned') === bigCityFilter)
      .slice()
      .sort((a, b) => (a.region_code || 'zzz').localeCompare(b.region_code || 'zzz'));
  }, [regions, stateFilter, bigCityFilter]);
  // Alias kept for existing City Regions card render logic below.
  const filteredRegions = scopedRegions;
  const availableRegionsForFilter = scopedRegions;

  const propertiesWithCoords = useMemo(
    () => properties.filter(isUsCoord),
    [properties]
  );

  // ── Map pins actually rendered — filtered by the Big City tab
  // selection so only that metro's pins appear (no visual overload).
  const regionIdsInScope = useMemo(
    () => new Set(scopedRegions.map((r) => r.region_id)),
    [scopedRegions],
  );
  const mapPropertiesWithCoords = useMemo(() => {
    return propertiesWithCoords.filter(
      (p) => p.region_id && regionIdsInScope.has(p.region_id),
    );
  }, [propertiesWithCoords, regionIdsInScope]);

  const colorByRegion = useMemo(() => {
    const map = {};
    regions.forEach((r) => { map[r.region_id] = r.color || '#C9A962'; });
    return map;
  }, [regions]);

  const geocodeOne = async (propertyId) => {
    try {
      const res = await fetch(`${API}/api/routes/properties/${propertyId}/geocode`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Geocode failed');
      else { toast.success(`Geocoded · ${data.city || ''}`); loadAll(); }
    } catch (_) { toast.error('Network error'); }
  };

  const assignRegion = async (propertyId, regionId) => {
    try {
      const res = await fetch(`${API}/api/routes/properties/${propertyId}/region`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ region_id: regionId || null }),
      });
      if (res.ok) { toast.success('Updated'); loadAll(); }
    } catch (_) { /* noop */ }
  };

  const sendDigests = async () => {
    if (!window.confirm('Send the weekly Property Health digest now to every PM?')) return;
    try {
      const res = await fetch(`${API}/api/routes/admin/send-pm-digests`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) toast.success(`${data.sent} digests sent.`);
      else toast.error(data.detail || 'Failed');
    } catch (_) { toast.error('Network error'); }
  };

  const [bulkGeocoding, setBulkGeocoding] = useState(false);
  const bulkGeocode = async () => {
    const ungeo = properties.filter((p) => !p.latitude).length;
    if (!ungeo) return toast.info('All properties already geocoded.');
    if (!window.confirm(`Geocode ${ungeo} properties? Throttled at 1/sec — will take ~${ungeo} seconds.`)) return;
    setBulkGeocoding(true);
    toast.info(`Geocoding ${ungeo} properties... please wait.`);
    try {
      const res = await fetch(`${API}/api/routes/admin/geocode-all`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Done · ${data.succeeded} succeeded, ${data.failed} failed, ${data.skipped_no_address} skipped.`);
        loadAll();
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (_) { toast.error('Network error'); }
    finally { setBulkGeocoding(false); }
  };

  // Seed the 15 DFW Metroplex regions for a US state in one click and
  // auto-assign every un-clustered property to its matching region by city.
  const [seedingState, setSeedingState] = useState(false);
  const seedStateRegions = async (state = 'TX') => {
    const ok = window.confirm(
      `Bulk-create the pre-defined business regions for ${state}? `
      + `For TX this adds the 15 DFW Metroplex regions (R01–R15) and auto-assigns `
      + `every property whose city matches a region. Existing manual region assignments are kept.`,
    );
    if (!ok) return;
    setSeedingState(true);
    try {
      const res = await fetch(`${API}/api/routes/regions/seed-state`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `${state}: ${data.regions_created} created, ${data.regions_updated} updated · `
          + `${data.properties_auto_assigned} properties auto-assigned.`,
        );
        loadAll();
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (_) { toast.error('Network error'); }
    finally { setSeedingState(false); }
  };

  // Delete every legacy region created with the old free-form 'New Region'
  // button (no `region_code`). Their properties are released to the
  // unassigned bucket so the next seed-state sweep snaps them to a canonical
  // R01–R15 region by city.
  const [purging, setPurging] = useState(false);
  const purgeUncodedRegions = async () => {
    const ok = window.confirm(
      'Delete every legacy region without an R-code (e.g. the old "North Dallas" / '
      + '"East Dallas" entries created before the DFW catalog)? Their properties '
      + 'will be released to "Unassigned" so they can re-snap to R01–R15 on the '
      + 'next Seed Texas Regions click.',
    );
    if (!ok) return;
    setPurging(true);
    try {
      const res = await fetch(`${API}/api/routes/regions/purge-uncoded`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        if (data.deleted === 0) toast.info('No legacy regions to purge — already clean.');
        else toast.success(`Purged ${data.deleted} legacy region${data.deleted === 1 ? '' : 's'}; released ${data.released_properties} properties.`);
        loadAll();
      } else {
        toast.error(data.detail || 'Failed');
      }
    } catch (_) { toast.error('Network error'); }
    finally { setPurging(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-routes-regions">
      {!readOnly && <RegionalTerritoryEditor regions={regions} />}
      {/* Header + actions */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <MapIcon className="h-6 w-6 text-[#FFD60A]" /> ROUTES & REGIONS
            </h2>
            <p className="text-sm text-zinc-400 mt-1">
              Group properties into city clusters (e.g., &ldquo;North Dallas: Lewisville, Carrollton, Addison&rdquo;). Click <strong className="text-[#C9A962]">Seed Texas Regions (DFW)</strong> to bulk-create all 15 DFW Metroplex Business Regions (R01–R15) and auto-assign properties to their matching region by city. The map color-codes everything by region.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {readOnly ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-zinc-700 text-zinc-400"
                data-testid="admin-rr-readonly-badge"
              >
                Read-only · {deptLabel || 'Department'}
              </span>
            ) : (
              <>
                <Button onClick={() => setRegionDialog({ open: true, edit: null })} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="region-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> New Region
                </Button>
                <Button
                  onClick={() => seedStateRegions('TX')}
                  disabled={seedingState}
                  className="bg-[#C9A962] text-black hover:opacity-90 font-bold"
                  data-testid="seed-tx-regions-btn"
                  title="Bulk-create all 15 DFW Metroplex Business Regions (R01–R15) for Texas and auto-assign existing properties by city."
                >
                  <MapIcon className={`h-4 w-4 mr-1 ${seedingState ? 'animate-pulse' : ''}`} />
                  {seedingState ? 'Seeding…' : 'Seed Texas Regions (DFW)'}
                </Button>
                <Button
                  onClick={purgeUncodedRegions}
                  disabled={purging}
                  variant="outline"
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                  data-testid="purge-legacy-regions-btn"
                  title="Delete legacy regions created with the old New Region button (no R-code), releasing their properties so they can re-snap to R01–R15."
                >
                  <Trash2 className={`h-4 w-4 mr-1 ${purging ? 'animate-pulse' : ''}`} />
                  {purging ? 'Purging…' : 'Purge Legacy Regions'}
                </Button>
                <Button onClick={() => setRouteDialog({ open: true })} className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold" data-testid="route-add-btn">
                  <RouteIcon className="h-4 w-4 mr-1" /> New Route
                </Button>
                <Button
                  onClick={bulkGeocode}
                  disabled={bulkGeocoding}
                  variant="outline"
                  className="border-[#FFD60A] text-[#FFD60A]"
                  data-testid="bulk-geocode-btn"
                >
                  <Compass className={`h-4 w-4 mr-1 ${bulkGeocoding ? 'animate-spin' : ''}`} />
                  {bulkGeocoding ? 'Geocoding...' : 'Bulk Geocode All'}
                </Button>
                <Button variant="outline" onClick={sendDigests} className="border-[#FFD60A] text-[#FFD60A]" data-testid="send-pm-digests-btn">
                  <Mail className="h-4 w-4 mr-1" /> Send PM Digests Now
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Map with Big City TAB LIST on top — Dallas · Houston · Austin ·
          San Antonio · Tyler. Clicking a tab focuses the map to that
          metro AND drives the Cities & Regions Explorer below to the
          same metro's regions/cities/properties.  Prevents visual
          overload by rendering only pins that belong to the selected
          metro. */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-0 overflow-hidden" data-testid="routes-map-card">
        {/* ── Big City tab strip (primary selector) ───────────────── */}
        <div
          className="bg-gradient-to-r from-[#0a0a0a] via-zinc-950 to-[#0a0a0a] border-b border-[#FFD60A]/40 p-3"
          data-testid="routes-map-bigcity-tabs"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {BIG_CITY_TABS.map((city) => {
                const active = bigCityFilter === city;
                return (
                  <button
                    key={city}
                    type="button"
                    onClick={() => setBigCityFilter(city)}
                    className={`px-4 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-widest transition-all border ${
                      active
                        ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                        : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                    }`}
                    data-testid={`routes-map-bigcity-tab-${city.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {city}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              {/* Secondary State selector kept for future multi-state
                  expansion — hidden behind a small badge, does not
                  compete with the primary Big City tabs. */}
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">State</span>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="h-7 rounded bg-zinc-950 border border-zinc-700 text-zinc-300 text-[11px] px-2 focus:outline-none focus:border-[#C9A962]"
                  data-testid="routes-map-state-filter"
                >
                  <option value="__ALL__">All</option>
                  {availableStates.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <Badge
                className="bg-emerald-700 text-white text-[11px] font-bold"
                data-testid="routes-map-pin-count"
              >
                {mapPropertiesWithCoords.length} pin{mapPropertiesWithCoords.length === 1 ? '' : 's'} · {bigCityFilter}
              </Badge>
            </div>
          </div>
        </div>

        {/* ── Map body ─────────────────────────────────────────────── */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Compass className="h-4 w-4 text-[#FFD60A]" />
              {bigCityFilter} Map · {mapPropertiesWithCoords.length} of {propertiesWithCoords.length} geocoded properties
            </h3>
            <div className="flex flex-wrap gap-2 text-[11px] text-zinc-300">
              {scopedRegions.map((r) => (
                <span key={r.region_id} className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                  {r.region_code || r.name}
                </span>
              ))}
            </div>
          </div>
          <button type="button" className="text-xs font-bold text-[#C9A962] py-2" aria-expanded={mapOpen} onClick={()=>setMapOpen(v=>!v)} data-testid="routes-map-toggle">{mapOpen ? 'Hide map' : 'Show map'}</button>
          {mapOpen && <div style={{ height: 420 }} className="rounded-lg overflow-hidden">
            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapFlyController
                pins={mapPropertiesWithCoords}
                bigCity={bigCityFilter}
                regionSelected={false}
              />
              {mapPropertiesWithCoords.map((p) => (
                <CircleMarker
                  key={p.property_id}
                  center={[p.latitude, p.longitude]}
                  radius={9}
                  pathOptions={{
                    color: '#0a0a0a',
                    weight: 1,
                    fillColor: colorByRegion[p.region_id] || '#888',
                    fillOpacity: 0.85,
                  }}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{p.property_name}</div>
                      <div className="text-zinc-700">{p.address}</div>
                      {p.region_name && <div className="mt-1"><Badge style={{ backgroundColor: colorByRegion[p.region_id] || '#888', color: '#fff' }}>{p.region_name}</Badge></div>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>}
          {mapPropertiesWithCoords.length === 0 && (
            <p className="text-[11px] text-zinc-500 italic text-center py-2" data-testid="routes-map-empty">
              No geocoded properties in {bigCityFilter} yet. Add properties via the Explorer below or click <strong className="text-[#C9A962]">Bulk Geocode All</strong>.
            </p>
          )}
        </div>
      </Card>

      {/* Cities & Regions explorer — expandable region cards + city
          list with one-click property registration. Registered
          properties auto-appear on the map above. Filtered to match
          the Big City tab picked above the map. */}
      <CitiesRegionsExplorer
        regions={regions}
        properties={properties}
        onPropertyCreated={loadAll}
        bigCity={bigCityFilter}
        readOnly={readOnly}
      />

      {/* Regions list + properties matrix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="regions-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h3 className="text-sm font-bold text-white">
              City Regions
              <span className="text-[10px] text-zinc-500 font-normal ml-2">
                {bigCityFilter} · {filteredRegions.length} region{filteredRegions.length === 1 ? '' : 's'}
              </span>
            </h3>
            <Badge
              className="bg-zinc-800 text-[#C9A962] text-[10px] font-mono"
              data-testid="regions-scope-badge"
            >
              Scope: {bigCityFilter}
            </Badge>
          </div>
          {filteredRegions.length === 0 ? (
            <p className="text-xs text-zinc-500 py-3 text-center" data-testid="regions-empty">
              {regions.length === 0
                ? 'No regions yet — click "New Region" to start.'
                : `No regions in ${bigCityFilter}. Pick another big city.`}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="regions-list">
              {filteredRegions.map((r) => (
                <li key={r.region_id} className="flex items-center justify-between bg-zinc-800 border border-zinc-700 rounded-lg p-3" data-testid={`region-row-${r.region_id}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white truncate">
                        {r.region_code ? <span className="text-[#C9A962] mr-1">{r.region_code}</span> : null}
                        {r.name}
                        {r.big_city ? <span className="text-[10px] text-zinc-400 ml-1.5">· {r.big_city}</span> : null}
                        {r.state ? <span className="text-[10px] text-zinc-500 ml-1">· {r.state}</span> : null}
                      </div>
                      <div className="text-[11px] text-zinc-400 truncate">
                        {r.cities?.join(' · ') || r.description || '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-zinc-700 text-white">{r.property_count} properties</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRegionDialog({ open: true, edit: r })}
                      className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962] hover:text-black font-semibold"
                      data-testid={`region-edit-btn-${r.region_id}`}
                    >
                      Edit
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="properties-region-matrix">
          <h3 className="text-sm font-bold text-white mb-3">Properties · Region & Geocoding</h3>
          <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {properties.map((p) => {
              const regionColor = colorByRegion[p.region_id] || '#71717a';
              return (
              <li key={p.property_id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-3" data-testid={`prop-row-${p.property_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    {/* Property name + region + city populated inline so
                        admin sees geo classification without opening a
                        detail view. Region uses the region's brand color. */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate" data-testid={`prop-name-${p.property_id}`}>
                        {p.property_name}
                      </span>
                      {p.region_name ? (
                        <Badge
                          className="text-[10px] uppercase tracking-wide text-black font-bold"
                          style={{ backgroundColor: regionColor }}
                          data-testid={`prop-region-badge-${p.property_id}`}
                        >
                          {p.region_name}
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-700 text-zinc-300 text-[10px] uppercase">
                          Region pending
                        </Badge>
                      )}
                      {p.city && (
                        <Badge
                          className="bg-zinc-900 border border-zinc-600 text-zinc-200 text-[10px]"
                          data-testid={`prop-city-badge-${p.property_id}`}
                        >
                          {p.city}{p.state ? `, ${p.state}` : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-400 truncate mt-0.5 flex items-center gap-1">
                      <MapPin className="h-3 w-3 flex-shrink-0 text-zinc-500" />
                      <span className="truncate">{p.address || '—'}</span>
                    </div>
                    {p.latitude ? (
                      <div className="text-[10px] text-emerald-400 mt-0.5">
                        ✓ Geocoded · {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                      </div>
                    ) : (
                      <div className="text-[10px] text-amber-300 mt-0.5">Not geocoded yet</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={p.region_id || 'none'} onValueChange={(v) => assignRegion(p.property_id, v === 'none' ? '' : v)}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white w-44 h-8 text-xs" data-testid={`prop-region-select-${p.property_id}`}>
                        <SelectValue placeholder="Region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No region —</SelectItem>
                        {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => geocodeOne(p.property_id)} className="border-zinc-700 text-xs h-8" data-testid={`prop-geocode-${p.property_id}`}>
                      {p.latitude ? 'Re-geocode' : 'Geocode'}
                    </Button>
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {/* Existing routes */}
      <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="routes-list-card">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <RouteIcon className="h-4 w-4 text-[#FFD60A]" /> Job Routes
        </h3>
        {routes.length === 0 ? (
          <p className="text-xs text-zinc-500 py-3 text-center">No routes yet — click &ldquo;New Route&rdquo; to build one.</p>
        ) : (
          <ul className="space-y-2" data-testid="routes-list">
            {routes.map((rt) => (
              <li key={rt.route_id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-3" data-testid={`route-row-${rt.route_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-bold text-white">{rt.name}</div>
                    <div className="text-[11px] text-zinc-400">
                      {rt.region_name && <Badge className="bg-zinc-700 mr-2">{rt.region_name}</Badge>}
                      {rt.property_ids.length} stops · {rt.scheduled_date || 'unscheduled'}
                    </div>
                    <div className="text-xs text-zinc-300 mt-1">
                      {(rt.properties_snapshot || []).map((p) => p.property_name).join(' → ')}
                    </div>
                  </div>
                  <Badge className={rt.status === 'draft' ? 'bg-zinc-600' : 'bg-emerald-600'}>{rt.status}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <RegionDialog
        open={regionDialog.open}
        edit={regionDialog.edit}
        onClose={() => setRegionDialog({ open: false, edit: null })}
        onSaved={loadAll}
      />
      <RouteDialog
        open={routeDialog.open}
        regions={regions}
        properties={properties}
        staff={staff}
        onClose={() => setRouteDialog({ open: false })}
        onSaved={loadAll}
      />
      {loading && <div className="text-center text-xs text-zinc-500">Loading…</div>}
    </div>
  );
}

function RegionDialog({ open, edit, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', cities: '', color: '', big_city: '', region_code: '' });
  useEffect(() => {
    if (edit) {
      setForm({
        name: edit.name || '',
        description: edit.description || '',
        cities: (edit.cities || []).join(', '),
        color: edit.color || '',
        big_city: edit.big_city || '',
        region_code: edit.region_code || '',
      });
    } else {
      setForm({ name: '', description: '', cities: '', color: '', big_city: '', region_code: '' });
    }
  }, [edit, open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Region name required');
    const payload = {
      name: form.name,
      description: form.description,
      cities: form.cities.split(',').map((s) => s.trim()).filter(Boolean),
      color: form.color || undefined,
      big_city: (form.big_city || '').trim() || undefined,
      region_code: (form.region_code || '').trim().toUpperCase() || undefined,
    };
    try {
      const url = edit ? `${API}/api/routes/regions/${edit.region_id}` : `${API}/api/routes/regions`;
      const res = await fetch(url, {
        method: edit ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success(edit ? 'Region updated' : 'Region created'); onSaved(); onClose(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch (_) { toast.error('Network error'); }
  };

  const remove = async () => {
    if (!edit || !window.confirm('Delete this region?')) return;
    try {
      const res = await fetch(`${API}/api/routes/regions/${edit.region_id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Region deleted'); onSaved(); onClose(); }
    } catch (_) { /* noop */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#3D3D3D]">{edit ? 'Edit Region' : 'New City Region'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="r-big-city">Big City</Label>
              <Input
                id="r-big-city" placeholder="Dallas | Austin | Houston | San Antonio | Tyler"
                value={form.big_city} onChange={(e) => setForm({ ...form, big_city: e.target.value })}
                data-testid="region-bigcity-input"
              />
            </div>
            <div>
              <Label htmlFor="r-code">Zone Code</Label>
              <Input
                id="r-code" placeholder="e.g. CT1, H2, ET1, R07"
                value={form.region_code} onChange={(e) => setForm({ ...form, region_code: e.target.value })}
                className="uppercase font-mono"
                data-testid="region-code-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="r-name">Zone Name *</Label>
            <Input id="r-name" placeholder="e.g., Tech Corridor North" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="region-name-input" />
          </div>
          <div>
            <Label htmlFor="r-cities">Cities (comma-separated)</Label>
            <Input id="r-cities" placeholder="Lewisville, Carrollton, Farmers Branch, Addison, Denton" value={form.cities} onChange={(e) => setForm({ ...form, cities: e.target.value })} data-testid="region-cities-input" />
          </div>
          <div>
            <Label htmlFor="r-desc">Description</Label>
            <Textarea id="r-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="r-color">Color (optional)</Label>
            <Input id="r-color" type="color" value={form.color || '#C9A962'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20 p-1" />
          </div>
        </div>
        <DialogFooter>
          {edit && <Button variant="outline" className="border-red-500 text-red-500" onClick={remove}>Delete</Button>}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="region-save-btn">
            <Save className="h-4 w-4 mr-1" /> {edit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RouteDialog({ open, regions, properties, staff, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', region_id: '', property_ids: [], assigned_supervisor_id: '', scheduled_date: '', notes: '',
  });

  useEffect(() => {
    if (open) setForm({ name: '', region_id: '', property_ids: [], assigned_supervisor_id: '', scheduled_date: '', notes: '' });
  }, [open]);

  const filteredProps = useMemo(
    () => form.region_id ? properties.filter((p) => p.region_id === form.region_id) : properties,
    [properties, form.region_id]
  );

  const togglePid = (pid) => {
    setForm((f) => ({
      ...f,
      property_ids: f.property_ids.includes(pid)
        ? f.property_ids.filter((x) => x !== pid)
        : [...f.property_ids, pid],
    }));
  };

  // Nearest-neighbor sort by lat/lng (greedy — good enough for small daily routes)
  const sortByProximity = () => {
    const selected = filteredProps.filter((p) => form.property_ids.includes(p.property_id) && p.latitude);
    if (selected.length < 2) return toast.info('Need at least 2 selected geocoded properties');
    const remaining = [...selected];
    const order = [remaining.shift()];
    while (remaining.length) {
      const last = order[order.length - 1];
      let bestIdx = 0, bestD = Infinity;
      remaining.forEach((p, i) => {
        const d = (p.latitude - last.latitude) ** 2 + (p.longitude - last.longitude) ** 2;
        if (d < bestD) { bestD = d; bestIdx = i; }
      });
      order.push(remaining.splice(bestIdx, 1)[0]);
    }
    setForm((f) => ({ ...f, property_ids: order.map((p) => p.property_id) }));
    toast.success('Sorted by proximity');
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Route name required');
    if (!form.property_ids.length) return toast.error('Select at least one property');
    try {
      const res = await fetch(`${API}/api/routes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Route created'); onSaved(); onClose(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch (_) { toast.error('Network error'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#3D3D3D]">New Job Route</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rt-name">Route name *</Label>
              <Input id="rt-name" placeholder="e.g., North Dallas — Tuesday Run" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="route-name-input" />
            </div>
            <div>
              <Label>Region (filter)</Label>
              <Select value={form.region_id || 'all'} onValueChange={(v) => setForm({ ...form, region_id: v === 'all' ? '' : v, property_ids: [] })}>
                <SelectTrigger data-testid="route-region-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Stops ({form.property_ids.length} selected)</Label>
              <Button size="sm" variant="outline" onClick={sortByProximity} data-testid="route-sort-btn">
                <Compass className="h-3 w-3 mr-1" /> Sort by proximity
              </Button>
            </div>
            <div className="border border-zinc-200 rounded max-h-60 overflow-y-auto">
              {filteredProps.length === 0 ? (
                <p className="text-xs text-zinc-500 text-center py-3">No properties in this region.</p>
              ) : filteredProps.map((p, idx) => {
                const orderIdx = form.property_ids.indexOf(p.property_id);
                return (
                  <label key={p.property_id} className="flex items-center gap-2 p-2 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50" data-testid={`route-prop-${p.property_id}`}>
                    <input type="checkbox" checked={orderIdx >= 0} onChange={() => togglePid(p.property_id)} />
                    {orderIdx >= 0 && <Badge className="bg-[#C9A962] text-black h-5 w-5 p-0 flex items-center justify-center text-[10px]">{orderIdx + 1}</Badge>}
                    <span className="text-sm flex-1">{p.property_name}</span>
                    <span className="text-[10px] text-zinc-500">{p.city || (p.latitude ? '✓' : 'no geo')}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Assign Supervisor</Label>
              <Select value={form.assigned_supervisor_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_supervisor_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="route-supervisor-select"><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.badge_number || s.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rt-date">Scheduled date</Label>
              <Input id="rt-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="route-date-input" />
            </div>
          </div>

          <div>
            <Label htmlFor="rt-notes">Notes</Label>
            <Textarea id="rt-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold" data-testid="route-save-btn">
            <Save className="h-4 w-4 mr-1" /> Create Route
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
